import { createSelector } from "reselect";

import { DeploymentPermission } from "constants/permissions";

import { getUserFullName } from "lib/names";
import { hasPermission } from "lib/permissions";

import { getActiveLivestockAgentDeployment } from "./deploymentActiveAgent";
import { getActiveRole, getLivestockAgents } from "./root";

export interface SaleRoleUserOption {
  value: number;
  label: string;
}

/**
 * Merge assigned user ids into a base option list so their chips render even
 * when the user isn't in the loaded agent directory (resolving names via
 * nameByUserId). Shared by the grid editor and the sale form.
 */
export const mergeAssignedUserOptions = (
  baseOptions: SaleRoleUserOption[],
  assignedUserIds: number[],
  nameByUserId: Record<number, string>,
  { sort = false }: { sort?: boolean } = {},
): SaleRoleUserOption[] => {
  const byValue = new Map<number, SaleRoleUserOption>();
  baseOptions.forEach(option => byValue.set(option.value, option));
  assignedUserIds.forEach(id => {
    if (!byValue.has(id)) {
      byValue.set(id, { value: id, label: nameByUserId[id] || "" });
    }
  });
  const merged = [...byValue.values()];
  return sort ? merged.sort((a, b) => a.label.localeCompare(b.label)) : merged;
};

interface SelectableAgent {
  isActive?: boolean;
  userId?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  deployments?: { id: number }[];
}

/**
 * Is the Manager / Assignee feature enabled for the active deployment?
 * Gates whether the columns are shown at all.
 */
export const selectCanViewSaleRoles = createSelector(
  [getActiveLivestockAgentDeployment],
  deployment =>
    hasPermission(deployment, DeploymentPermission.featureSaleRoles),
);

/**
 * Can the current user set/clear managers & assignees? Requires the feature
 * flag plus the dedicated edit permission. Field agents lack the permission.
 */
export const selectCanEditSaleRoles = createSelector(
  [getActiveLivestockAgentDeployment],
  deployment =>
    hasPermission(deployment, DeploymentPermission.featureSaleRoles) &&
    hasPermission(
      deployment,
      DeploymentPermission.canEditSaleManagerAndAssignee,
    ),
);

/**
 * Selectable people for the Manager / Assignee dropdowns: the active
 * deployment's livestock agents, as {value: userId, label: name} options.
 */
export const selectSaleRoleUserOptions = createSelector(
  [getActiveLivestockAgentDeployment, getLivestockAgents],
  (deployment, agentsById): SaleRoleUserOption[] => {
    const deploymentId = deployment?.id;
    if (!deploymentId) {
      return [];
    }
    const optionsByUserId = new Map<number, SaleRoleUserOption>();
    (Object.values(agentsById) as SelectableAgent[]).forEach(agent => {
      if (
        agent?.isActive &&
        agent?.userId &&
        (agent.deployments || []).some(d => d.id === deploymentId)
      ) {
        optionsByUserId.set(agent.userId, {
          value: agent.userId,
          label:
            getUserFullName(agent.firstName, agent.lastName) ||
            agent.email ||
            "",
        });
      }
    });
    return [...optionsByUserId.values()].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  },
);

/**
 * Resolve a manager/assignee user id to a display name from the loaded agent
 * directory (with the current user as a fallback so a fresh self-assign
 * resolves before the directory loads). Mirrors getSaleStatusById.
 */
export const selectSaleRoleNameByUserId = createSelector(
  [getLivestockAgents, getActiveRole],
  (agentsById, activeRole): Record<number, string> => {
    const nameByUserId: Record<number, string> = {};
    (Object.values(agentsById) as SelectableAgent[]).forEach(agent => {
      if (agent?.userId) {
        nameByUserId[agent.userId] =
          getUserFullName(agent.firstName, agent.lastName) || agent.email || "";
      }
    });
    const userId = activeRole?.user_id;
    if (userId && !nameByUserId[userId]) {
      const fullName = getUserFullName(
        activeRole.first_name,
        activeRole.last_name,
      );
      nameByUserId[userId] = fullName || activeRole.email || "";
    }
    return nameByUserId;
  },
);

/**
 * Sale-role options plus the current user, so the creator is always
 * selectable (e.g. the pre-filled assignee) even if the deployment's agent
 * list hasn't loaded.
 */
export const selectSaleRoleUserOptionsWithCurrentUser = createSelector(
  [selectSaleRoleUserOptions, getActiveRole],
  (options, activeRole): SaleRoleUserOption[] => {
    const userId = activeRole?.user_id;
    if (!userId || options.some(option => option.value === userId)) {
      return options;
    }
    const fullName = getUserFullName(
      activeRole.first_name,
      activeRole.last_name,
    );
    return [
      ...options,
      { value: userId, label: fullName || activeRole.email || "You" },
    ];
  },
);
